import GiftIcon from '@images/icons/gift.svg'
import { tr } from 'mmfintech-commons'
import React, { createContext, type ReactNode, useContext, useState } from 'react'
import { PromoCodeInput } from '../PromoCodeInput'
import './previewLayout.scss'

interface PromoCodeConfig {
  title?: string
  icon?: string
  placeholder?: string
  label?: string
  buttonText?: string
  onSuccess?: (code: string) => void
  onClear?: () => void
  validatePromoCode?: (code: string) => Promise<void>
  isLoading?: boolean
  error?: string
  /** Whether promo code is currently applied (derived from preview response) */
  isApplied?: boolean
}

interface PreviewLayoutProps {
  children: ReactNode
  /**
   * Whether to show the promo code section
   * When false or undefined, hides the aside completely
   */
  showPromo?: boolean
  /**
   * Promo code configuration (required when showPromo is true)
   */
  promoCodeConfig?: PromoCodeConfig
}

// Context to share state with MobilePromo component
const PreviewLayoutContext = createContext<{
  showPromo: boolean
  promoCodeProps: any
  title: string
  icon: string
} | null>(null)

export const PreviewLayout: React.FC<PreviewLayoutProps> & {
  MobilePromo: React.FC
} = ({ children, showPromo = false, promoCodeConfig }) => {
  // Only track input value internally
  const [inputValue, setInputValue] = useState('')

  // Default values with optional overrides
  const title = promoCodeConfig?.title || tr('FRONTEND.BANK_TRANSFER.PREVIEW.PROMO_CODE', 'Add promo code')
  const icon = promoCodeConfig?.icon || GiftIcon
  const placeholder =
    promoCodeConfig?.placeholder || tr('FRONTEND.BANK_TRANSFER.PREVIEW.PROMO_PLACEHOLDER', 'Enter promo code')
  const label = promoCodeConfig?.label || tr('FRONTEND.BANK_TRANSFER.PREVIEW.PROMO_LABEL', 'Enter code')
  const buttonText = promoCodeConfig?.buttonText || tr('FRONTEND.BANK_TRANSFER.PREVIEW.PROMO_BUTTON', 'Add')

  // Derive from props
  const isApplied = promoCodeConfig?.isApplied ?? false
  const success = isApplied ? tr('FRONTEND.PROMO.CODE_APPLIED', 'Code applied') : ''

  const handleApplyPromoCode = async (code: string) => {
    if (!promoCodeConfig?.validatePromoCode) {
      return
    }
    await promoCodeConfig.validatePromoCode(code)
    promoCodeConfig.onSuccess?.(code)
  }

  const handleClearPromoCode = async () => {
    if (promoCodeConfig?.validatePromoCode) {
      try {
        await promoCodeConfig.validatePromoCode('')
      } finally {
        setInputValue('')
        promoCodeConfig?.onClear?.()
      }
    } else {
      setInputValue('')
      promoCodeConfig?.onClear?.()
    }
  }

  const promoCodeProps = {
    value: inputValue,
    onChange: (_: string, value: string) => setInputValue(value),
    onApply: handleApplyPromoCode,
    onClear: handleClearPromoCode,
    isLoading: promoCodeConfig?.isLoading || false,
    error: promoCodeConfig?.error || '',
    success,
    placeholder,
    label,
    buttonText,
    isApplied
  }

  return (
    <PreviewLayoutContext.Provider
      value={{
        showPromo,
        promoCodeProps,
        title,
        icon
      }}
    >
      <div className={`preview-layout ${!showPromo ? 'preview-layout--no-aside' : ''}`}>
        <div className='preview-layout-content'>{children}</div>
        {showPromo && (
          <aside className='preview-aside'>
            <div className='aside-content'>
              <div className='aside-section-title'>
                <img src={icon} alt='' className='gift-icon' />
                {title}
              </div>
              <PromoCodeInput {...promoCodeProps} />
            </div>
          </aside>
        )}
      </div>
    </PreviewLayoutContext.Provider>
  )
}

// Compound component for mobile promo
PreviewLayout.MobilePromo = () => {
  const context = useContext(PreviewLayoutContext)

  // Only show mobile promo when showPromo is true
  if (!context?.showPromo) {
    return null
  }

  return (
    <div className='preview-aside-mobile'>
      <div className='aside-section-title'>
        <img src={context?.icon} alt='' className='gift-icon' />
        {context?.title}
      </div>
      <PromoCodeInput {...context?.promoCodeProps} />
    </div>
  )
}
